import { useCallback } from 'react';
import type { ConditionEvaluationObject } from '@unifyapps/defs/types/conditionEvaluationObject';
import { produce } from 'immer';
import { ExecutionInstance } from '../../filter/api/ExecutionInstance';
import { UIFilter } from '../../filter/api/UIFilter';
import { ConditionEvaluatorImpl } from '../../filter/ConditionEvaluatorHelperImpl';
import { recursiveUpdateWithSelector } from '../../utils/recursiveUpdate';
import { sanitizeFilters } from '../../utils/sanitizeFilters';
import { isConditionalOperator } from '../../context/DependencyGraphContext/handlers';
import { useGetNoCodeComputedData } from '../computeContext/useGetNoCodeComputedData';

const conditionEvaluator = new ConditionEvaluatorImpl();

export function getConditionOperatorEvaluationFunc({
  conditionEvaluationObj,
  context,
  getNoCodeComputedData,
}: {
  conditionEvaluationObj?: ConditionEvaluationObject;
  context: Record<string, unknown>;
  getNoCodeComputedData?: ExecutionInstance['getComputedValue'];
}) {
  const instance = new ExecutionInstance(context, getNoCodeComputedData);

  if (!conditionEvaluationObj) {
    return undefined;
  }

  if (conditionEvaluationObj.type === 'boolean') {
    return conditionEvaluationObj.payload;
  }

  try {
    const sanitizedFilters = produce(conditionEvaluationObj.payload, sanitizeFilters);
    const uiFilter = UIFilter.createUIFilter(sanitizedFilters);
    const filter = uiFilter.toFilter();
    return conditionEvaluator.evaluate(filter, instance);
  } catch (e) {
    console.error('Error evaluating condition', e, conditionEvaluationObj.payload);
  }
}

export function useEvaluateConditionalOperator() {
  const { getNoCodeComputedData } = useGetNoCodeComputedData();

  const evaluateConditionalOperator = useCallback(
    (params: {
      conditionEvaluationObj: ConditionEvaluationObject | undefined;
      context: Record<string, unknown>;
    }) => {
      return getConditionOperatorEvaluationFunc({
        ...params,
        getNoCodeComputedData,
      });
    },
    [getNoCodeComputedData],
  );

  return { evaluateConditionalOperator };
}

export function getConditionEvaluationFunc<T>(
  props: Record<string, unknown>,
  context: Record<string, unknown>,
  getNoCodeComputedData?: ExecutionInstance['getComputedValue'],
) {
  return recursiveUpdateWithSelector({
    obj: props,
    updater: (key, value) => {
      const conditionEvaluationObj = value as ConditionEvaluationObject | undefined;

      return getConditionOperatorEvaluationFunc({
        conditionEvaluationObj,
        context,
        getNoCodeComputedData,
      });
    },
    // valueSelector is a function that takes a key and a value and returns a boolean if the value should be updated
    valueSelector: isConditionalOperator,
  }) as T;
}

export function useComputeConditionEvaluationGetter() {
  const { getNoCodeComputedData } = useGetNoCodeComputedData();

  return useCallback(
    <T>(props: Record<string, unknown>, context: Record<string, unknown>) => {
      return getConditionEvaluationFunc<T>(props, context, getNoCodeComputedData);
    },
    [getNoCodeComputedData],
  );
}
